import Component from '@ux/component';
import React from 'react';
import PropTypes from 'prop-types';
import Button from '@ux/button';
import Modal from '@ux/modal';
import Tooltip from '@ux/tooltip';
import CurrencySelector from '@ux/currency-selector';
import MarketSelector from '@ux/market-selector';
import Logo from '@ux/logo';
import EmailForm from './email-form';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { breakpoints } from './enums';
import assign from 'object-assign';
import Link from '@ux/link';
import golfToEid from 'golf-to-eid';
import WeChatIcon from './wechat-icon.js';
import FacebookIcon from './facebook-icon.js';
import InstagramIcon from './instagram-icon.js';
import TwitterIcon from './twitter-icon.js';
import YouTubeIcon from './youtube-icon.js';
import ChevDoubleRightLt from '@ux/icon/chevron-dbl-right-lt';
import memoize from 'memoize-one';

const vatInclusionCodes = {
  ALL: 'Sales:Footer:VAT:Disclaimer',
  NONE: 'Sales:Footer:VAT:DisclaimerNone',
  TAXES: 'Sales:Footer:VAT:DisclaimerTaxes',
  ICANN: 'Sales:Footer:VAT:DisclaimerICANN'
};

// use the id to map to the correct socialmedia link component.
const socialMediaIcons = {
  'Sales:Footer:SocialMedia:FacebookIcon': FacebookIcon,
  'Sales:Footer:SocialMedia:TwitterIcon': TwitterIcon,
  'Sales:Footer:SocialMedia:InstagramIcon': InstagramIcon,
  'Sales:Footer:SocialMedia:YouTubeIcon': YouTubeIcon
};

/**
 * Render and expose the Product navigation as a Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced Product Nav.
 * @implements {React.Component}
 * @api public
 */
class SalesFooter extends Component {
  constructor() {
    super(...arguments);

    this.memoizedLegalSection = memoize(this.buildLegalSection);
    this.memoizedSocialMediaLinks = memoize(this.buildSocialMediaLinks);
    this.memoizedSupportPhone = memoize(this.buildSupportPhone);
    this.memoizedVatDisclaimer = memoize(this.buildVatDisclaimer);

    this.state = {
      ...this.state,
      goDaddyStoreModal: false,
      selected: null
    };
  }

  /**
   * Prefix for generated EIDs
   *
   * @type {String}
   * @readonly
   * @private
   */
  get eidPrefix() {
    return this.props.seechange
      ? 'uxp.hyd.sales_footer_seechange'
      : 'uxp.hyd.sales_footer';
  }

  get footerLinkEid() {
    return {
      prefix: this.eidPrefix,
      postfix: 'link.click'
    };
  }

  /**
   * construct an li wrapped a tag from a manifest-render-service navigation item
   *
   * @param {Object} items structure containing link data
   * @param {Object} options options structure
   * @returns {Array} a configured anchor tag
   * @private
   */
  linkListBuilder(items = [], options = {}) {
    const className = [this.classNames(options.className)];
    const active = options.active || false;

    return items.map((item, i) => {
      const local = className.slice();
      if (active && ~active.indexOf(item.id)) {
        local.push('active');
      }

      let onClick;
      if (
        item.caption === 'Sales:Footer:AboutGD:GoDaddyStore' ||
        (item.id && ~item.id.indexOf('GoDaddyStore'))
      ) {
        onClick = this.showGoDaddyStoreModal.bind(this);
      }

      // `title` refers to the `title` attribute on the anchor in Link, so we need to strip it here and pass it as a
      // child if present
      const itemWithoutTitle = {
        ...item,
        title: void 0
      };
      const linkChildren = item.title ? <span>{item.title}</span> : void 0;
      const captionOrId = item.caption || item.id;
      return (
        <p key={ captionOrId + i } className={ local.filter(Boolean).join(' ') }>
          <Link
            { ...itemWithoutTitle }
            { ...options }
            href={ item.href || '#' }
            onClick={ onClick }
            data-eid={ golfToEid(captionOrId, this.footerLinkEid) }>
            {linkChildren}
          </Link>
        </p>
      );
    });
  }

  /**
   * Updates the state associated with this instance to display a modal
   * with content related to the GoDaddy Store
   * @param   {Event} evt React Synthetic event for Click.
   * @returns {Boolean} Value indicating to stop propagation.
   */
  showGoDaddyStoreModal(evt) {
    evt.preventDefault();
    this.setState({
      goDaddyStoreModal: true
    });
    return false;
  }

  /**
   * Returns a React Element to display the GoDaddy Store model if necessary
   * @returns {React.ReactNode} The UXCore2 modal or null (if appropriate).
   */
  renderGoDaddyStoreModal() {
    if (!this.state.goDaddyStoreModal) {
      return null;
    }

    // 'Shared:Common:OK' isn't localized in eldorado.
    // Remark: do we need to make a new string for "OK"?
    const title = <FormattedMessage id='PC:Modal:GodaddyStore:HeaderTitle' />;
    const footer = (
      <Button
        design='primary'
        onClick={ () => {
          window.open(this.props.urls.store.href);
          this.setState({
            goDaddyStoreModal: false
          });
        } }
        data-eid='uxp.hyd.sales_footer.godaddy_store.modal.ok.link.click'>
        <FormattedMessage id='Shared:Common:OK' />
      </Button>
    );

    return (
      <Modal
        title={ title }
        onClose={ () => {
          this.setState({
            goDaddyStoreModal: false
          });
        } }
        footer={ footer }>
        <p>
          <FormattedMessage id='PC:Modal:GodaddyStore:HeaderText' />
        </p>
      </Modal>
    );
  }

  /**
   * @param {Object} currItem containing link data
   * Returns a React Element to display the WeChat Tooltip if necessary
   * @returns {React.ReactNode} The UXCore2 FormattedMessage with Tooltip.
   */
  renderWeChat(currItem) {
    const props = this.props;
    const market = props.market;
    const cdn = props.urls && props.urls.cdn && props.urls.cdn.href;

    const { imgPath } = currItem.options || {};
    const lowercaseMarket = market.toLowerCase();
    const weChatQR = altText => (
      <img
        className='wechat-qr'
        src={ `${cdn}${imgPath.replace('{market}', lowercaseMarket)}` }
        height='247'
        width='218'
        alt={ altText }
      />
    );

    return (
      <FormattedMessage id='Sales:Footer:SocialMedia:WeChatIcon'>
        {text => (
          <Tooltip
            data-eid={ golfToEid(currItem.caption || currItem.id, {
              prefix: 'uxp.hyd.sales_footer',
              postfix: 'button.click'
            }) }
            className='footer-wechat'
            aria-label={ text }
            tabIndex={ 0 }
            id='footer-wechat'
            text={
              <FormattedMessage id='Sales:Footer:SocialMedia:WeChatIcon'>
                { altText => (
                  <WeChatIcon className='wechat-icon' altText={ altText } />
                ) }
              </FormattedMessage>
            }
            message={
              currItem.caption ? (
                <IntlProvider locale={ props.market } messages={ props.messages }>
                  <FormattedMessage id={ currItem.caption }>
                    { weChatQR }
                  </FormattedMessage>
                </IntlProvider>
              ) : (
                weChatQR(currItem.title)
              )
            }
            title={
              currItem.caption ? (
                <IntlProvider locale={ props.market } messages={ props.messages }>
                  <FormattedMessage id={ currItem.caption } />
                </IntlProvider>
              ) : (
                currItem.title
              )
            }></Tooltip>
        )}
      </FormattedMessage>
    );
  }

  /**
   * Renders links in the legal section
   *
   * @param {Object}   props Full set of props for this component.
   * @param {Object[]} links Array of objects that have a caption and href property
   * @param {String} [liClass] Optional classname to include on li elements
   * @api private
   * @returns {React.ReactNode} li for each item in links
   */
  buildLegalLinks(props, links, liClass) {
    if (!Array.isArray(links) || !links.length > 0) {
      return null;
    }

    return links.map((link, index) => {
      // plid=3153 is used when "we do not know" the plid in which case we remove the privacy policy link.
      if (
        props.privateLabelId === 3153 &&
        ~[
          'Shared:LegalBar:LinkList:PrivacyPolicy',
          'Shared:LegalBar:LinkList:ResellerPrivacyPolicy'
        ].indexOf(link.id)
      ) {
        return;
      }

      return (
        <li key={ index } className={ liClass }>
          <Link
            urlArgs={ props.urlArgs }
            href={ link.href }
            data-eid={ golfToEid(link.id, this.footerLinkEid) }>
            {link.title}
          </Link>
        </li>
      );
    });
  }

  selectFooterCol(selected) {
    // we should not be able to toggle in a screen larger than a phablet
    // expand/collapse is no longer an option with seechange
    if (this.props.seechange || !this.isMobile) {
      return;
    }

    // just close if we select something currently open
    if (selected === this.state.selected) {
      return this.setState({
        selected: null
      });
    }

    this.setState({
      selected
    });
  }

  /**
   * Render footer links in sections
   *
   * @param   {Object} props Full set of props for this component.
   * @returns {React.ReactNodeArray} A set of <Dropdown/> components.
   * @api private
   */
  buildFooterLinks(props) {
    const { seechange } = props;
    const upperLinks = props.footer.filter(item => {
      const captionOrId = item.caption || item.id;
      if (
        captionOrId !== 'Sales:Footer:MobileApps:Heading' &&
        captionOrId !== 'Sales:Footer:SocialMedia:Heading'
      ) {
        return item;
      }
    });

    const navItemChildren = upperLinks.map(item => {
      return this.linkListBuilder(item.children, {
        className: 'footernav-item',
        active: props.active,
        urlArgs: props.urlArgs
      });
    });

    return upperLinks.map((curr, index) => {
      const opened =
        !this.isMobile || props.seechange || this.state.selected === index;
      const columnBreak =
        (index + 1) % 2 === 0 && seechange ? (
          <div className='hidden-sm-down hidden-lg-up clearfix'></div>
        ) : null;
      return (
        <React.Fragment key={ index }>
          <div
            className={ this.classNames(
              !seechange ? 'col-md-2' : 'col-lg-2',
              seechange ? 'col-md-6' : '',
              'col-sm-12',
              'col-12',
              'footer-links-column',
              opened ? 'opened' : 'closed'
            ) }>
            <button
              type='button'
              onClick={ () => this.selectFooterCol(index) }
              className='column-heading font-primary-bold'>
              {curr.caption ? (
                <FormattedMessage id={ curr.caption } />
              ) : (
                curr.title
              )}
            </button>
            {navItemChildren[index]}
          </div>
          { columnBreak }
        </React.Fragment>
      );
    });
  }

  /**
   * Builds a set of links that have corresponding images
   * @param  {Object} items Object that contains an array of children with link data
   * @param  {String} [spanClass] Class to be added to the span.  Must be pre-namespaced by the caller.
   * @param  {Object} [urlArgs]   Args to append as a query to the link
   * @returns {React.ReactElement[]} </li> for each link with an </img>
   * @api private
   */
  buildLinksWithImg(items, spanClass, urlArgs) {
    const props = this.props;
    const cdn = props.urls && props.urls.cdn && props.urls.cdn.href;
    if (!cdn || !Array.isArray(items.children)) {
      return [];
    }

    return items.children.map((currItem, index) => {
      // className comes from `eldorado-data`
      const { className, imgPath } = currItem.options || {};

      if (!imgPath) return;
      const imgFromAlt = altText => (
        <img src={ `${cdn}${imgPath}` } alt={ altText } />
      );

      if (
        currItem.caption === 'Sales:Footer:SocialMedia:WeChatQRCode' ||
        currItem.id === 'Sales:Footer:SocialMedia:WeChatQRCode'
      ) {
        return <li key={ index }>{ this.renderWeChat(currItem) }</li>;
      }

      return (
        <li key={ index }>
          <Link
            urlArgs={ urlArgs }
            href={ currItem.href }
            data-eid={ golfToEid(currItem.caption || currItem.id, {
              prefix: 'uxp.hyd.sales_footer',
              postfix: 'link.click'
            }) }>
            <span
              className={ this.classNames(
                spanClass,
                className
              ) }>
              {currItem.caption ? (
                <FormattedMessage id={ currItem.caption }>
                  { imgFromAlt }
                </FormattedMessage>
              ) : (
                imgFromAlt(currItem.title)
              )}
            </span>
          </Link>
        </li>
      );
    });
  }

  /**
   * Returns a React Element representing the social media links for the specified `props`.
   *
   * @param   {Object} footer Links in footer for social media.
   * @param   {Object} urlArgs For link building.
   * @returns {React.ReactElement[]} <li/> for each item in props.footer that is a child of Sales:Footer:SocialMedia:Heading
   * @api private
   */
  buildSocialMediaLinks(footer, urlArgs) {
    const socialMedia = footer.filter(
      x =>
        x.caption === 'Sales:Footer:SocialMedia:Heading' ||
        (x.id && ~x.id.indexOf('SocialMedia'))
    )[0];
    if (!socialMedia || !Array.isArray(socialMedia.children)) {
      return [];
    }

    return socialMedia.children.map((icon, index) => {
      // className comes from `sitecore`
      const { className } = icon.options || {};
      const IconComp = socialMediaIcons[icon.id];

      if (icon.id === 'Sales:Footer:SocialMedia:WeChatQRCode') {
        return <li key={ index }>{ this.renderWeChat(icon) }</li>;
      }

      if (typeof socialMediaIcons[icon.id] !== 'function') {
        return null;
      }

      return (
        <li key={ index }>
          <Link
            urlArgs={ urlArgs }
            title={ icon.title }
            href={ icon.href }
            data-eid={ golfToEid(icon.caption || icon.id, {
              prefix: 'uxp.hyd.sales_footer',
              postfix: 'link.click'
            }) }>
            <span className={ this.classNames('footer-social-icon', className) }>
              <IconComp altText={ icon.title } />
            </span>
          </Link>
        </li>
      );
    });
  }

  /**
   * Renders the legal section.
   *
   * @param   {Array} legal legal objects from props.
   * @param   {Boolean} seechange seechange treatment.
   * @param   {Object} urlArgs for building legal links.
   * @param   {Object} urls for building legal links.
   * @param   {Object} features for handling donotsell link.
   * @param   {String} privateLabelId priatelabelid.
   * @returns {React.ReactNode} div containing disclaimer, legal links, do not sell link, and copyright
   * @api private
   */
  buildLegalSection(legal, seechange, urlArgs, urls, features, privateLabelId) { // eslint-disable-line max-params
    const sections = {};
    legal.map((section, index) => {
      // eslint-disable-line max-statements
      let firstCaption = '';
      let firstChild = {};
      if (
        section.children &&
          Array.isArray(section.children) &&
          section.children.length > 0
      ) {
        firstChild = section.children[0];
        firstCaption = firstChild.caption || firstChild.id;
      }
      const sectionCaptionOrId = section.caption || section.id;

      // Legal disclaimer
      if (~firstCaption.indexOf('Disclaimer')) {
        const disclaimer = (
          <p key={ index } className='footer-disclaimer'>
            <span>
              {section.caption ? (
                <FormattedMessage id={ section.caption } />
              ) : (
                section.title
              )}{' '}
            </span>
            <Link
              urlArgs={ urlArgs }
              href={ section.children[0].href }
              data-eid={ golfToEid(firstCaption, this.footerLinkEid) }>
              {firstChild.caption ? (
                <FormattedMessage id={ firstCaption } />
              ) : (
                firstChild.title
              )}
            </Link>
          </p>
        );
        sections.disclaimer = disclaimer;
        return disclaimer;
      } else if (~firstCaption.indexOf('LinkList')) {
        // legal links
        const links = (
          <ul key={ index } className='list-inline footer-legal-links'>
            {this.buildLegalLinks({
              urlArgs,
              privateLabelId },
            section.children,
            seechange ? 'list-inline-item' : void 0
            )}
          </ul>
        );
        sections.links = links;
        return links;
      } else if (
        sectionCaptionOrId &&
          ~sectionCaptionOrId.indexOf('CopyrightMessage')
      ) {
        // copyright
        const copyright = (
          <p key={ index } className='footer-copyright'>
            { section.title }
          </p>
        );
        sections.copyright = copyright;
        return copyright;
      }
    })
      .filter(Boolean);

    // handle the ccpa link
    const hasDoNotSellReq = features.doNotSellLink;
    const doNotSellId = 'Sales:Footer:Privacy:DoNotSell';
    const doNotSellMessage = (
      <FormattedMessage id='Sales:Footer:Privacy:DoNotSell' />
    );

    let doNotSell = null;

    if (hasDoNotSellReq && urls.cookies.href) {
      doNotSell = (
        <div className='footer-do-not-sell'>
          <Link
            urlArgs={ urlArgs }
            href={ urls.cookies.href }
            data-eid={ golfToEid(doNotSellId, this.footerLinkEid) }>
            { doNotSellMessage }
          </Link>
        </div>
      );
    }

    return this.buildSeeChangeLegalSection(sections, doNotSell);
  }

  buildSeeChangeLegalSection(sections, doNotSell) {
    return (
      <div className='footer-seechange-legal'>
        <div className='container-fluid'>
          <div className='row'>
            <div className='col-lg-5 col-12 pull-lg-right'>
              { sections.links }
              { doNotSell }
            </div>
            <div className='col-lg-7 col-12'>{sections.copyright}</div>
            <div className='col-lg-6 col-12 legal-right'>
              {sections.disclaimer}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Builds the display hero for the Support Phone in the current market
   * @param   {Object} supportContacts Data structure normalized by @ux/manifest-render-service
   *                                  (i.e. ux.eldorado.data.props.supportContacts).
   * @returns {React.ReactNode} Fully realized Support Phone UX.
   */
  buildSupportPhone(supportContacts = {}) {
    const number = supportContacts.technicalSupportPhone || '';
    const phone = {
      href: 'tel:' + number.replace(/[()\s-]/g, ''),
      number
    };

    //
    // Disable max-len for this block because we need some things on the same
    // line to preserve spaces
    //
    /* eslint-disable max-len */
    if (this.isMobile) {
      return (
        <div className='support-short'>
          <FormattedMessage
            id='Sales:Footer:SupportBar:MobileCallSupportTeam'
          />
          <a href={ phone.href } className='support-footer-number'>
            {phone.number}
          </a>
        </div>
      );
    }

    return (
      <div>
        <FormattedMessage id='Sales:Footer:SupportBar:NeedHelp' />{ ' ' }
        <FormattedMessage id='Sales:Footer:SupportBar:CallSupportTeam' />{ ' ' }
        <a href={ phone.href } className='support-footer-number'>
          {phone.number}
        </a>
      </div>
    );
    /* eslint-enable max-len */
  }

  /**
   * Builds the vatDisclaimer in the current market
   * @param   {Object} marketData from props
   * @param   {String} market from props
   * @param   {Object} vatInclusion from props
   *
   * @returns {React.ReactNode} Fully realized vatDisclaimer.
   */
  buildVatDisclaimer(marketData, market, vatInclusion) {
    const marketInfo = marketData[market];
    const displayTaxesAndFees =
      marketInfo &&
      vatInclusion &&
      vatInclusion.indexOf(market) > -1 &&
      marketInfo.displayTaxesAndFees;

    if (displayTaxesAndFees) {
      return (
        <div className='row footer-vat-disclaimer'>
          <div>
            <FormattedMessage id={ vatInclusionCodes[displayTaxesAndFees] } />
          </div>
          <hr className='main-hr' />
        </div>
      );
    }
    return null;

  }

  /**
   * Add eventListeners for breakpoints.
   *
   * @api private
   */
  componentDidMount() {
    this.breakpoint(...breakpoints);
  }

  /**
   * Remove the eventListeners of breakpoints.
   *
   * @api private
   */
  componentWillUnmount() {
    this.breakup();
  }

  renderGoDaddyGuides() {
    const props = this.props;

    return (
      <Link
        { ...props.urls.help }
        eid='uxp.hyd.sales_footer_seechange.godaddy_guides.link.click'
        className='footer-godaddy-guides clearfix'>
        <span className='guides-left'>
          <span className='godaddy-guides-line guides-line-1'>
            <FormattedMessage id='Sales:Footer:GoDaddyGuides:WeTrulyLoveTakingYourCall' />
          </span>
          <span className='godaddy-guides-line guides-line-2'>
            <FormattedMessage id='Sales:Footer:GoDaddyGuides:LogoAlt'>
              {altText => (
                <img
                  className='gd-guides-svg'
                  src={
                    props.urls.cdn.href + 'ux/sales-header/gd-guides-new.svg'
                  }
                  alt={ altText }
                />
              )}
            </FormattedMessage>
          </span>
        </span>
        <span className='godaddy-guides-line guides-line-3 guides-right'>
          <FormattedMessage id='Sales:Footer:GoDaddyGuides:LogoAlt'>
            {altText => (
              <img
                className='gd-guides-arrow'
                src={ props.urls.cdn.href + 'ux/sales-header/care-arrow.svg' }
                alt={ altText }
              />
            )}
          </FormattedMessage>
        </span>
      </Link>
    );
  }

  renderSeeChangeTopSection() {
    const { messages, env, seechange, features } = this.props;

    // Desktop rendering
    if (~['desktop', 'large', 'xlarge'].indexOf(this.state.breakpoint)) {
      return (
        <div className='footer-seechange-top-section'>
          <div className='container-fluid'>
            <div className='row'>
              <div className='footer-seechange-top-left col-md-8'>
                {features.footerEmailForm && (
                  <EmailForm
                    messages={ messages }
                    env={ env }
                    seechange={ seechange }
                  />
                )}
              </div>
              <div className='footer-seechange-top-right col-md-4'>
                { this.renderGoDaddyGuides() }
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Tablet and below rendering (re-orders sections)
    return (
      <div className='footer-seechange-top-section'>
        <div className='container-fluid'>
          <div className='row'>
            <div className='footer-seechange-top-right col-xs-12'>
              { this.renderGoDaddyGuides() }
            </div>
            <div className='footer-seechange-top-left col-xs-12'>
              {features.footerEmailForm && (
                <EmailForm
                  messages={ messages }
                  env={ env }
                  seechange={ seechange }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderFooterLowerSeechange(
    socialMediaLinks,
    marketSelectorProps,
    currencySelectorProps
  ) {
    const props = this.props;

    return (
      <>
        <div className='footer-lower'>
          <div className='footer-lower-container'>
            <Logo
              go={ props.features.go }
              market={ props.market }
              messages={ props.messages }
              logos={ props.logos }
              href={ props.urls.home.href }
              privateLabelId={ 1 /* This is GoDaddy only */ }
              showCountryNameOnLogo={ false }
              spanClass='logo'
              className='logo-link'
              isMobile={ false /* Always show full logo here */ }
              id='footer-lower-logo'
            />
            <div className='hr hidden-lg-up' />
            <div className='selectors'>
              <MarketSelector { ...marketSelectorProps } />
              <CurrencySelector { ...currencySelectorProps } />
            </div>
            {socialMediaLinks && socialMediaLinks.length > 0 ? (
              <div className='follow-us'>
                <ul className='list-inline social-media-links'>
                  { socialMediaLinks }
                </ul>
              </div>
            ) : null}
          </div>
        </div>
        <div className='hr hidden-md-down' />
      </>
    );
  }

  /**
   * Renders the div for the footer support bar. Content varies based on feature
   * and presence of wechat link.
   * @returns {React.ReactElement} The div for the support bar
   */
  renderSupportBar() {
    const props = this.props;
    if (props.features.footerSupportBar === false) return;

    let content = this.memoizedSupportPhone(props.supportContacts);

    if (props.urls.wechat) {
      const weChatLearnMoreId = 'Sales:Footer:SupportBar:WeChatLearnMore';
      content = (
        <div className='wechat-support-wrapper'>
          <span className='wechat-support-text'>
            <FormattedMessage id='Sales:Footer:SupportBar:WeChat' />
          </span>
          <span className='chevron-learn-more'>
            <FormattedMessage id={ weChatLearnMoreId }>
              {title => (
                <Link
                  href={ props.urls.wechat.href }
                  title={ title }
                  eid={ golfToEid(weChatLearnMoreId, this.footerLinkEid) }
                  className='wechat-learn-more'
                  urlArgs={ props.urlArgs }>
                  <span className='chevron-dbl-right-lt'>
                    <ChevDoubleRightLt width={ 26 } height={ 26 } />
                  </span>
                  { title }
                </Link>
              )}
            </FormattedMessage>
          </span>
        </div>
      );
    }

    return (
      <div className='footer-support'>
        <div className='container'>
          <h4>{ content }</h4>
        </div>
      </div>
    );
  }

  /**
   * Renders footer
   *
   * @api private
   * @returns {React.ReactNode} namespaced footer
   */
  render() {
    const props = this.props;
    const { features, footer, legal, marketData, market, privateLabelId, seechange, urlArgs, urls, vatInclusion } = props;
    const legalSection = this.memoizedLegalSection(legal, seechange, urlArgs, urls, features, privateLabelId);
    const socialMediaLinks = this.memoizedSocialMediaLinks(footer, urlArgs);
    const vatDisclaimer = this.memoizedVatDisclaimer(marketData, market, vatInclusion);

    const marketSelectorProps = assign(
      {
        urlArgs: props.urlArgs,
        manifest: props.manifest,
        eidPrefix: this.eidPrefix,
        urls: props.urls,
        messages: props.messages,
        currentMarket: props.market,
        numColumns: this.state.breakpoint === 'tablet' ? 2 : 4,
        currentPage: props.currentPage,
        lockScrollOnOpen: true,
        marketData: props.marketData
      },
      props.marketSelector
    );

    const currencySelectorProps = {
      messages: props.messages,
      eidPrefix: this.eidPrefix,
      market: props.market,
      currency: props.currency,
      urls: props.urls,
      privateLabelId: props.privateLabelId
    };

    // This is a placeholder div that is used for Tealium's privacy
    // policy content injection. Do not modify this without speaking
    // to the Tealium team (see: #tealium-support on Slack)
    const tealiumPrivacyPlaceholder = <div id='gtm_privacy'></div>;

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <div className='footer footer-seechange'>
          { this.renderSupportBar() }
          { seechange && this.renderSeeChangeTopSection() }
          <div className='footer-link-section'>
            <div>
              {
                <div className='container-fluid'>
                  { vatDisclaimer }
                  <div className='row footer-links'>
                    { this.buildFooterLinks(props) }
                  </div>
                </div>
              }
            </div>
            {
              this.renderFooterLowerSeechange(
                socialMediaLinks,
                marketSelectorProps,
                currencySelectorProps
              )
            }
          </div>
          { legalSection }
          { this.renderGoDaddyStoreModal() }
          { tealiumPrivacyPlaceholder }
        </div>
      </IntlProvider>
    );
  }
}

SalesFooter.propTypes = {
  currency: PropTypes.string,
  currentPage: PropTypes.string,
  env: PropTypes.string,

  features: PropTypes.shape({
    go: PropTypes.bool,
    doNotSellLink: PropTypes.bool,
    footerEmailForm: PropTypes.bool,
    footerSupportBar: PropTypes.bool
  }),

  footer: PropTypes.array,
  legal: PropTypes.array,
  logos: PropTypes.object,
  manifest: PropTypes.string,
  market: PropTypes.string.isRequired,
  marketData: PropTypes.object,
  marketSelector: PropTypes.object,
  messages: PropTypes.object.isRequired,
  privateLabelId: PropTypes.number.isRequired,
  seechange: PropTypes.bool,
  supportContacts: PropTypes.object,
  urls: PropTypes.object,
  urlArgs: PropTypes.object,
  vatInclusion: PropTypes.object,
  year: PropTypes.number
};

SalesFooter.defaultProps = {
  features: {},
  marketData: {},
  privateLabelId: 1,
  seechange: true,
  year: new Date().getFullYear()
};

export default SalesFooter;
